import { API_ENDPOINTS, } from '../constants/api-endpoints.constants';
import { NETWORK_CONNECTIVITY } from '../constants/network-connectivity.constants';
import { LogLevel } from '../constants/log.constants';

import { Logger } from './logger';

export class NetworkConnectivity {

  private logger: Logger;
  private networkConnectivityPingUrl: URL;

  constructor(
    logLevel: LogLevel,
    baseApiUrl: string) {

    this.logger = new Logger(this.constructor.name, logLevel);
    this.networkConnectivityPingUrl = new URL(`${baseApiUrl}/${API_ENDPOINTS.networkConnectivity.ping}`);
  }

  async checkConnectivity(): Promise<boolean> {

    const logPrefix = 'checkConnectivity';

    if (!navigator.onLine) {
      // navigator.onLine being false is a reliable indicator
      this.logger.info(`${logPrefix}: browser reports being offline => assume offline`);
      return false;
    }

    // navigator.onLine being true is NOT a reliable indicator
    this.logger.info(`${logPrefix}: browser reports being online => check via server call`);
    return await this.checkConnectivityViaServerCall();
  }

  private async checkConnectivityViaServerCall(): Promise<boolean> {

    const logPrefix = 'checkConnectivityViaServerCall';

    const abortController = new AbortController();
    const abortTimeoutId = setTimeout(() => abortController.abort(), NETWORK_CONNECTIVITY.pingTimeoutMs);

    try {
      const networkConnectivityPingRequest: RequestInit = {
        method: 'HEAD', // HEAD is often lighter than GET/POST
        cache: 'no-store',
        signal: abortController.signal // Set a timeout for a fast failure
      };
      const networkConnectivityPingResponse = await fetch(this.networkConnectivityPingUrl, networkConnectivityPingRequest);

      if (networkConnectivityPingResponse.ok) {
        this.logger.info(`${logPrefix}: online and server reachable => assume online`, networkConnectivityPingResponse);
        return true;
      }

      this.logger.info(`${logPrefix}: online but server error => assume offline`, networkConnectivityPingResponse);
      return false;
    }
    catch (e) {
      this.logger.info(`${logPrefix}: offline or network error / timeout / DNS resolution issue => assume offline`, e);
      return false;
    }
    finally {
      clearTimeout(abortTimeoutId);
    }
  }
}
